<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import Chart from 'primevue/chart';

interface Props {
  disciplines: string[];
  color: string;
  showMore: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disciplines: () => [],
  color: 'blue',
  showMore: false,
});

const { disciplines, color, showMore } = toRefs(props);

const colors = [
  {
    style: 'blue',
    hex: ['#75C6E4', '#75C6E4', '#75C6E4', '#75C6E4', '#AAD9EA', '#40B3DD'],
  },
  {
    style: 'purple',
    hex: ['#A19DDC', '#A19DDC', '#A19DDC', '#A19DDC', '#C3C2E6', '#7E78D2'],
  },
  {
    style: 'pink',
    hex: ['#DDB5D0', '#DDB5D0', '#DDB5D0', '#DDB5D0', '#E5D0DF', '#D49BC1'],
  },
  {
    style: 'orange',
    hex: ['#EEBE79', '#EEBE79', '#EEBE79', '#EEBE79', '#EFD5AD', '#EDA745'],
  },
];

const chart = ref({
  datasets: [
    {
      data: [],
      backgroundColor: [],
    },
  ],
});

watch(
  () => [showMore.value, disciplines.value],
  () => defineChartData(),
);

const hasDisciplines = computed(() => Object.keys(disciplines.value).length);

const defineChartData = () => {
  const chartObject = {
    data: disciplines.value.map(({ discipline }) => discipline.total),
    backgroundColor: colors.find((item) => color.value === item.style).hex,
  };

  chart.value.datasets[0] = chartObject;
};

const disciplineTotalStyle = () => `text-color-${color.value} text-lg font-semibold`;
</script>

<template>
  <div v-if="showMore" class="grid m-0 border-round surface-100 fadein">
    <div v-if="hasDisciplines" class="col m-0">
      <div class="grid m-0 p-0">
        <div class="col m-0 p-0 pb-1">
          <Chart type="doughnut" :data="chart" />
        </div>
      </div>
      <div v-for="({ discipline }, key) in disciplines" :key="key" class="grid pt-1 pl-1">
        <div class="col-1 m-0 p-1 pr-8">
          <span :class="disciplineTotalStyle(key)" v-text="new Intl.NumberFormat().format(discipline.total)" />
        </div>
        <div class="col m-0 p-1">
          <span :class="`inline-block text-color-${color} text-sm`" style="margin-top: 2px" v-text="discipline.name" />
        </div>
      </div>
    </div>
    <div v-else class="col p-7 text-center text-gray-500 text-sm">No data for Disciplines</div>
  </div>
</template>
